// ThemeContext.js - React context for theme management
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { THEMES, getThemeCSSVariables } from '../utils/themes';

const ThemeContext = createContext(null);

// Text scale limits
const MIN_TEXT_SCALE = 0.8;
const MAX_TEXT_SCALE = 1.6;
const DEFAULT_TEXT_SCALE = 1.0;

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('popArt');
  const [textScale, setTextScale] = useState(DEFAULT_TEXT_SCALE);
  
  // Use refs for touch handling to avoid stale closures
  const initialDistance = useRef(null);
  const scaleAtPinchStart = useRef(DEFAULT_TEXT_SCALE);
  const currentScaleRef = useRef(DEFAULT_TEXT_SCALE);
  const isPinchOnImage = useRef(false);

  // Check if touch is on a panel image (should be handled by lightbox instead)
  const isTouchOnPanelImage = (touch) => {
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!element) return false;
    
    // Check if the touch is on the image container or within it
    return element.closest('.panel-image-container.tappable') !== null ||
           element.closest('.image-lightbox-overlay') !== null;
  };

  // Apply theme CSS variables to document root
  const applyTheme = useCallback((themeId) => {
    const cssVars = getThemeCSSVariables(themeId);
    const root = document.documentElement;
    
    Object.entries(cssVars).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Add theme class to body for theme-specific CSS
    document.body.className = document.body.className
      .replace(/theme-\w+/g, '')
      .trim();
    document.body.classList.add(`theme-${themeId}`);
  }, []);

  // Apply text scale CSS variable
  const applyTextScale = useCallback((scale) => {
    const root = document.documentElement;
    root.style.setProperty('--text-scale', scale.toString());
  }, []);

  // Change theme
  const changeTheme = useCallback((themeId) => {
    if (THEMES[themeId]) {
      setCurrentTheme(themeId);
      applyTheme(themeId);
      try {
        localStorage.setItem('ribbonRiderTheme', themeId);
      } catch (e) {
        // localStorage not available
      }
    }
  }, [applyTheme]);

  // Change text scale (can be called from pinch zoom or buttons)
  const changeTextScale = useCallback((scale) => {
    const clampedScale = Math.max(MIN_TEXT_SCALE, Math.min(MAX_TEXT_SCALE, scale));
    const roundedScale = Math.round(clampedScale * 100) / 100;
    
    currentScaleRef.current = roundedScale;
    setTextScale(roundedScale);
    applyTextScale(roundedScale);
    
    try {
      localStorage.setItem('ribbonRiderTextScale', roundedScale.toString());
    } catch (e) {
      // localStorage not available
    }
  }, [applyTextScale]);

  // Set up pinch zoom event listeners
  useEffect(() => {
    const getDistance = (touches) => {
      if (touches.length < 2) return null;
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        // Check if either touch point is on a panel image
        const touch1OnImage = isTouchOnPanelImage(e.touches[0]);
        const touch2OnImage = isTouchOnPanelImage(e.touches[1]);
        
        if (touch1OnImage || touch2OnImage) {
          // Pinch started on image - don't handle text scaling
          isPinchOnImage.current = true;
          initialDistance.current = null;
          return;
        }
        
        isPinchOnImage.current = false;
        initialDistance.current = getDistance(e.touches);
        scaleAtPinchStart.current = currentScaleRef.current;
      }
    };

    const handleTouchMove = (e) => {
      // Skip if pinch started on image
      if (isPinchOnImage.current) return;
      
      if (e.touches.length === 2 && initialDistance.current) {
        const currentDistance = getDistance(e.touches);
        
        if (currentDistance && initialDistance.current > 0) {
          const scaleMultiplier = currentDistance / initialDistance.current;
          let newScale = scaleAtPinchStart.current * scaleMultiplier;
          newScale = Math.max(MIN_TEXT_SCALE, Math.min(MAX_TEXT_SCALE, newScale));
          newScale = Math.round(newScale * 100) / 100;
          
          // Update ref and apply immediately
          currentScaleRef.current = newScale;
          
          // Apply to CSS
          document.documentElement.style.setProperty('--text-scale', newScale.toString());
          
          // Update React state (for the indicator)
          setTextScale(newScale);
        }
      }
    };

    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) {
        // Reset image pinch flag
        isPinchOnImage.current = false;
        
        if (initialDistance.current) {
          initialDistance.current = null;
          
          // Save the final scale
          try {
            localStorage.setItem('ribbonRiderTextScale', currentScaleRef.current.toString());
          } catch (err) {
            // localStorage not available
          }
        }
      }
    };

    const options = { passive: true };
    
    document.addEventListener('touchstart', handleTouchStart, options);
    document.addEventListener('touchmove', handleTouchMove, options);
    document.addEventListener('touchend', handleTouchEnd, options);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []); // Empty deps - handlers use refs

  // Load saved preferences on mount
  useEffect(() => {
    let savedTheme = 'popArt';
    let savedTextScale = DEFAULT_TEXT_SCALE;
    
    try {
      savedTheme = localStorage.getItem('ribbonRiderTheme') || 'popArt';
      const storedScale = localStorage.getItem('ribbonRiderTextScale');
      if (storedScale) {
        savedTextScale = parseFloat(storedScale);
      }
    } catch (e) {
      // localStorage not available
    }
    
    if (THEMES[savedTheme]) {
      setCurrentTheme(savedTheme);
      applyTheme(savedTheme);
    } else {
      applyTheme('popArt');
    }
    
    if (savedTextScale >= MIN_TEXT_SCALE && savedTextScale <= MAX_TEXT_SCALE) {
      currentScaleRef.current = savedTextScale;
      setTextScale(savedTextScale);
      applyTextScale(savedTextScale);
    } else {
      applyTextScale(DEFAULT_TEXT_SCALE);
    }
  }, [applyTheme, applyTextScale]);

  const value = {
    currentTheme,
    theme: THEMES[currentTheme],
    changeTheme,
    allThemes: THEMES,
    textScale,
    changeTextScale,
    minTextScale: MIN_TEXT_SCALE,
    maxTextScale: MAX_TEXT_SCALE,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;